import React, { useCallback } from "react";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Button,
} from "@mui/material";
import Modal from "../../../shared/components/ui/Modal";
import {
  VALIDATOR_REQUIRE,
  VALIDATOR_USPHONENUMBER,
} from "../../../shared/utils/validators";
import { validate } from "../../../shared/utils/validators";

const USER_STATUS_OPTIONS = ["Enabled", "Disabled"];
const SMS_MODE_OPTIONS = ["Disabled", "Enabled"];
const TIMEZONE_OPTIONS = [
  { value: "PKT", label: "PKT - Pakistan Standard Time (GMT+05:00)" },
  { value: "GMT", label: "GMT - Greenwich Mean Time (GMT+00:00)" },
  { value: "UTC", label: "UTC - Coordinated Universal Time (GMT+00:00)" },
  { value: "EST", label: "EST - Eastern Standard Time (GMT-05:00)" },
  { value: "CST", label: "CST - Central Standard Time (GMT-06:00)" },
  { value: "MST", label: "MST - Mountain Standard Time (GMT-07:00)" },
  { value: "PST", label: "PST - Pacific Standard Time (GMT-08:00)" },
];

const FIELD_HEIGHT = 56;
const TEXT_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    height: FIELD_HEIGHT,
  },
};
const SELECT_HEIGHT = FIELD_HEIGHT;
const SELECT_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 280,
    },
  },
};

const EditUserModal = ({
  show,
  formState,
  inputHandler,
  selectHandler,
  onCancel,
  onSubmit,
  isProcessing,
  errorMessage,
  voicemailNumbers = [],
  selectedVoicemailLabel = "",
}) => {
  const handleTextInputChange = useCallback((event, handler, validators) => {
    const { id, value } = event.target;
    handler(id, value, validate(value, validators));
  }, []);

  const isFormValid =
    formState.inputs.firstName.isValid &&
    formState.inputs.lastName.isValid &&
    (formState.inputs.smsMode.value === "Disabled" ||
      formState.inputs.mobileNumber.isValid);

  return (
    <Modal
      show={show}
      className="!w-[85%] shadow !rounded-2xl max-sm:!p-6"
      bodyClass="md:!px-0 pr-2"
      header={
        <h2 className="text-xl font-bold text-primary text-left">
          Edit User
        </h2>
      }
      description={
        <p className="text-md text-darkgray mt-1 text-left">
          Edit a user account
        </p>
      }
      footer={
        <div className="flex flex-col gap-2 w-full">
          {errorMessage && (
            <p className="text-sm text-red-500 text-right">{errorMessage}</p>
          )}
          <div className="flex gap-3 justify-end">
            <Button onClick={onCancel} variant="outlined">
              CANCEL
            </Button>
            <Button
              onClick={onSubmit}
              disabled={!isFormValid || isProcessing}
              variant="contained"
            >
              {isProcessing ? "UPDATING..." : "UPDATE"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4 !px-0">
        <div className="flex flex-col md:flex-row gap-4">
          <TextField
            id="firstName"
            label="First Name"
            placeholder="Enter First Name"
            variant="outlined"
            fullWidth
            value={formState.inputs.firstName.value}
            onChange={(e) =>
              handleTextInputChange(e, inputHandler, [VALIDATOR_REQUIRE()])
            }
            error={
              !formState.inputs.firstName.isValid &&
              formState.inputs.firstName.value !== ""
            }
            focused
            sx={TEXT_FIELD_SX}
          />
          <TextField
            id="lastName"
            label="Last Name"
            placeholder="Enter Last Name"
            variant="outlined"
            fullWidth
            value={formState.inputs.lastName.value}
            onChange={(e) =>
              handleTextInputChange(e, inputHandler, [VALIDATOR_REQUIRE()])
            }
            error={
              !formState.inputs.lastName.isValid &&
              formState.inputs.lastName.value !== ""
            }
            focused
            sx={TEXT_FIELD_SX}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <FormControl fullWidth>
            <InputLabel id="userStatus-label" focused>User Status</InputLabel>
            <Select
              id="userStatus"
              labelId="userStatus-label"
              value={formState.inputs.userStatus.value}
              sx={{ height: SELECT_HEIGHT }}
              MenuProps={SELECT_MENU_PROPS}
              input={
                <OutlinedInput
                  label="User Status"
                  className="text-primary"
                  sx={{ height: SELECT_HEIGHT }}
                  focused
                />
              }
              onChange={(e) => {
                const value = e.target.value;
                selectHandler("userStatus", value, value !== "");
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span className="text-content">Select User Status</span>;
                }
                return <span className="text-content">{selected}</span>;
              }}
            >
              {USER_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <FormControl fullWidth>
            <InputLabel id="smsMode-label" focused>SMS Mode</InputLabel>
            <Select
              id="smsMode"
              labelId="smsMode-label"
              value={formState.inputs.smsMode.value}
              sx={{ height: SELECT_HEIGHT }}
              MenuProps={SELECT_MENU_PROPS}
              input={
                <OutlinedInput
                  label="SMS Mode"
                  className="text-primary"
                  sx={{ height: SELECT_HEIGHT }}
                  focused
                />
              }
              onChange={(e) => {
                const value = e.target.value;
                selectHandler("smsMode", value, value !== "");
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span className="text-content">Select SMS Mode</span>;
                }
                return <span className="text-content">{selected}</span>;
              }}
            >
              {SMS_MODE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            id="mobileNumber"
            label="Mobile Number"
            placeholder="Enter Mobile Number"
            variant="outlined"
            className="!w-full"
            value={formState.inputs.mobileNumber.value}
            onChange={(e) =>
              handleTextInputChange(
                e,
                inputHandler,
                formState.inputs.smsMode.value !== "Disabled"
                  ? [VALIDATOR_USPHONENUMBER()]
                  : [],
              )
            }
            error={
              !formState.inputs.mobileNumber.isValid &&
              formState.inputs.mobileNumber.value !== "" &&
              formState.inputs.smsMode.value !== "Disabled"
            }
            focused
            disabled={formState.inputs.smsMode.value === "Disabled"}
            sx={TEXT_FIELD_SX}
          />
        </div>
        <div className="flex flex-row gap-4">
          <FormControl fullWidth>
            <InputLabel id="voicemailNumberId-label" focused shrink>
              Voicemail Number
            </InputLabel>
            <Select
              id="voicemailNumberId"
              labelId="voicemailNumberId-label"
              value={formState.inputs.voicemailNumberId.value}
              sx={{ height: SELECT_HEIGHT }}
              MenuProps={SELECT_MENU_PROPS}
              input={
                <OutlinedInput
                  label="Voicemail Number"
                  className="text-primary"
                  sx={{ height: SELECT_HEIGHT }}
                  focused
                />
              }
              onChange={(e) => {
                const value = e.target.value;
                selectHandler("voicemailNumberId", value, true);
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span className="text-content">Select optional voicemail number</span>;
                }

                const selectedRecord = voicemailNumbers.find(
                  (record) => String(record.id) === String(selected),
                );
                if (!selectedRecord) {
                  return (
                    <span className="text-content">
                      {selectedVoicemailLabel || selected}
                    </span>
                  );
                }

                const phoneValue = selectedRecord.phone_number || selectedRecord.phoneNumber || "";
                const friendlyName = selectedRecord.friendly_name || selectedRecord.friendlyName || "";
                const label = friendlyName && friendlyName !== phoneValue
                  ? `${friendlyName} (${phoneValue})`
                  : phoneValue;

                return <span className="text-content">{label || selected}</span>;
              }}
            >
              {voicemailNumbers.length === 0 ? (
                <MenuItem value="" disabled>
                  <em>None available - purchase a number first</em>
                </MenuItem>
              ) : voicemailNumbers.map((record) => {
                const phoneValue = record.phone_number || record.phoneNumber || "";
                const friendlyName = record.friendly_name || record.friendlyName || "";
                const label = friendlyName && friendlyName !== phoneValue
                  ? `${friendlyName} (${phoneValue})`
                  : phoneValue;

                return (
                  <MenuItem key={record.id} value={String(record.id)}>
                    {label || "-"}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="timeZone-label" focused>Time Zone</InputLabel>
            <Select
              id="timeZone"
              labelId="timeZone-label"
              value={formState.inputs.timeZone.value}
              sx={{ height: SELECT_HEIGHT }}
              MenuProps={SELECT_MENU_PROPS}
              input={
                <OutlinedInput
                  label="Time Zone"
                  className="text-primary"
                  sx={{ height: SELECT_HEIGHT }}
                  focused
                />
              }
              onChange={(e) => {
                const value = e.target.value;
                selectHandler("timeZone", value, value !== "");
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span className="text-content">Select Time Zone</span>;
                }
                const selectedOption = TIMEZONE_OPTIONS.find(opt => opt.value === selected);
                return <span className="text-content">{selectedOption ? selectedOption.label : selected}</span>;
              }}
            >
              {TIMEZONE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>
    </Modal>
  );
};

export default EditUserModal;
